import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';

import useQuestionnaire from 'hooks/useQuestionnaire';
import { checkSpanishLocale, cn, extractLabelAndComment } from 'lib/utils';
import { useQuestionnaireStore } from 'store/questionnaire';
import QuestionnaireContainer from './components/QuestionnaireContainer';
import Icon from 'components/icon/Icon';
import { EMOTION_ICON_COLORS } from 'lib/constants';
import Checkmark from './components/Checkmark';

const icons = ['depressed', 'sad', 'neutral', 'happy'];

export default function StressLevels() {
  const questionData = useQuestionnaireStore((store) => store.questionData);

  const isSpanish = checkSpanishLocale();

  const { onSubmit, isLoading } = useQuestionnaire();

  const [levelIndex, setLevelIndex] = useState<number>(0);

  const englishChoices = JSON.parse(questionData?.eng_choices || '{}');
  const spanishChoices = JSON.parse(questionData?.spanish_choices || '{}');

  const stressLevels: { en: string; es: string; icon: string }[] = useMemo(
    () =>
      englishChoices?.length
        ? englishChoices.map((choice: string, index: number) => ({
            en: choice,
            es: spanishChoices[index],
            icon: icons[index],
          }))
        : [],
    [englishChoices, spanishChoices]
  );

  const levelsWithLableComment = useMemo(
    () =>
      stressLevels?.length
        ? stressLevels.map((level) => ({
            ...level,
            en: extractLabelAndComment(level.en),
            es: extractLabelAndComment(level.es),
            icon: level.icon,
          }))
        : [],
    [stressLevels]
  );

  useEffect(() => {
    const getInitialIndex = () => {
      if (
        questionData?.user_eng_choices ||
        questionData?.user_spanish_choices
      ) {
        const labels: string[] = levelsWithLableComment.map(
          (langLevel) => langLevel.en.label
        );
        const userChoices =
          questionData?.[
            isSpanish ? 'user_spanish_choices' : 'user_eng_choices'
          ];
        let choice = '';
        try {
          const parsedChoices = JSON.parse(userChoices);
          choice = parsedChoices[0];
        } catch (error) {
          console.log(error);
        }

        const answer = choice.split('<comment>')[0] as string;
        const index = labels.indexOf(answer.trim());
        return index > 0 ? index : 2;
      }

      return 2;
    };
    const index = getInitialIndex();
    setLevelIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionData]);

  const onContinue = () => {
    const stressItem = stressLevels[levelIndex];
    onSubmit({
      choice_value: JSON.stringify([stressItem.en]),
      spanish_choice_value: JSON.stringify([stressItem.es]),
      questionData,
    });
  };

  return (
    <QuestionnaireContainer
      questionData={questionData}
      isLoading={isLoading}
      onContinue={onContinue}
      questionNumber={5}
    >
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-2 xl:gap-6'>
        {stressLevels?.length
          ? levelsWithLableComment.map((level, index) => {
              const isSelected = index === levelIndex;
              const selectedColor =
                EMOTION_ICON_COLORS[index % EMOTION_ICON_COLORS.length];

              return (
                <motion.div
                  key={level.icon}
                  onClick={() => setLevelIndex(index)}
                  className={cn(
                    'w-full border-[2px] rounded-2xl px-4 py-2 flex items-center justify-between cursor-pointer text-[#656D76] gap-4',
                    isSelected && 'border-primary text-primary bg-[#E1EBFF]'
                  )}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className='flex items-center gap-2'>
                    <div className='shrink-0'>
                      <Icon
                        icon={level.icon}
                        size={52}
                        color={isSelected ? selectedColor : '#656D76'}
                      />
                    </div>
                    <p className='text-sm leading-5'>
                      <span className='font-semibold'>
                        {level?.[isSpanish ? 'es' : 'en']?.label}
                      </span>{' '}
                      [{level?.[isSpanish ? 'es' : 'en']?.comment}]
                    </p>
                  </div>

                  <Checkmark isSelected={isSelected} />
                </motion.div>
              );
            })
          : []}
      </div>
    </QuestionnaireContainer>
  );
}
